import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { format } from "date-fns"
import { Modal } from "react-bootstrap"
import Select from "react-select"
import DatePicker, { CalendarContainer } from "react-datepicker";
import {
  updateJobServiceTransition,
  checkoutJobService,
  assignSurveyor,
  unassignSurveyor,
  assignPrimarySurveyor,
  unassignPrimarySurveyor
} from "requesters/jobServiceRequester"
import { getSurveyors } from "requesters/performanceReportRequester"
import { toast } from "react-toastify"
import SweetAlert from "react-bootstrap-sweetalert"
import { getBaseUrl } from "../../../reducers"

const MOBILE_BREAKPOINT = 767

const buildAssignSurveyorCalendarContainer = isMobile => ({ className, children }) => {
  if (isMobile) {
    return (
      <div className="assign-surveyor-datepicker-mobile">
        <CalendarContainer className={className}>
          {children}
        </CalendarContainer>
      </div>
    )
  }

  return (
    <CalendarContainer className={className}>
      {children}
    </CalendarContainer>
  )
}

const AssignSurveyorModal = ({
  show, onHide, jobService, reloadData, scheduledAssignment, userInitColorRendering, handleOpenCheckOutModal
}) => {
  const {
    assignments: originalAssignments,
    scheduledAt,
    primarySurveyorAssignmentEndAt,
    canAssign
  } = scheduledAssignment
  const { id, attributes: { surveyorsList, primarySurveyorId, estimatedHours } } = jobService
  const baseUrl = useSelector(getBaseUrl)
  const [assignments, setAssignments] = useState(originalAssignments)
  const [surveyor, setSurveyor] = useState(null)
  const [role, setRole] = useState(null)
  const [startAt, setStartAt] = useState(new Date(scheduledAt))
  const [endAt, setEndAt] = useState(new Date(primarySurveyorAssignmentEndAt))
  const [filteredSurveyors, setFilteredSurveyors] = useState([])
  const [showMessage, setShowMessage] = useState(false)
  const [alertTitle, setAlertTitle] = useState("")
  const [alertText, setAlertText] = useState("")
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  )
  const primaryAssignmentOfDay = assignments.find(ass => ass.role === "primary" && (ass.surveyorId === primarySurveyorId || ass.linkedToPrimarySurveyor))

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const assignSurveyorCalendarContainer = buildAssignSurveyorCalendarContainer(isMobile)

  const assignSurveyorDatePickerProps = {
    calendarContainer: assignSurveyorCalendarContainer,
    popperClassName: "assign-surveyor-datepicker-popper",
    withPortal: isMobile,
    popperPlacement: "bottom",
    timeIntervals: 15,
    ...(isMobile && {
      popperModifiers: {
        preventOverflow: {
          enabled: true,
          escapeWithReference: false,
          boundariesElement: "viewport",
          padding: 10
        },
        flip: {
          enabled: true,
          behavior: ["bottom", "top"]
        },
        offset: {
          enabled: true,
          offset: "0, 8px"
        }
      }
    })
  }

  const resetError = () => {
    setShowMessage(false)
    setAlertTitle("")
    setAlertText("")
  }

  const surveyorRoles = [
    {
      label: "Secondary Surveyor",
      value: "secondary",
    },
    {
      label: "Job Assistant",
      value: "assistant",
    },
  ]

  const toastOptions =  {
    toastId: "container-inner",
    containerId: "main"
  }

  const getAllSurveyors = () => {
    getSurveyors(baseUrl)
      .then(res => {
        const options = []
        res.data.forEach(s => options.push({ label: `${s[0]} ${s[1]}`, value: s[2] }))
        const assignedSurveyors = assignments.map(a => a.surveyorId)
        setFilteredSurveyors(options.filter(s => ![...assignedSurveyors, primarySurveyorId].includes(s.value)))
      })
      .catch(err => console.error(err))
  }

  useEffect(() => {
    setFilteredSurveyors([])

    if (role?.value === "secondary") {
      const assignedSurveyors = assignments.map(a => a.surveyorId)
      const newSurveyorOptions = surveyorsList.filter(s => ![...assignedSurveyors, primarySurveyorId].includes(s.value))
      setFilteredSurveyors(newSurveyorOptions)
    } else {
      getAllSurveyors()
    }
  }, [role, assignments, primarySurveyorId])

  const createSurveyorAssignment = () => {
    const dateStartString = moment(startAt).format("DD/MM/YYYY HH:mm:ss")
    const dateEndString = moment(endAt).format("DD/MM/YYYY HH:mm:ss")
    const params = {
      surveyor_id: surveyor.value,
      role: role.value,
      booking_date: scheduledAt,
      start_at: dateStartString,
      end_at: dateEndString,
    }
    assignSurveyor(baseUrl, id, params)
      .then(res => {
        if (res.status === 200) {
          reloadData()
          setSurveyor(null)
          setRole(null)
        } else {
          setShowMessage(true)
          setAlertTitle("Secondary Surveyor cannot be added!")
          const apiErrors = res?.error?.response?.data?.errors
          const apiErrorDetail = Array.isArray(apiErrors)
            ? apiErrors[0]?.detail || apiErrors[0]
            : apiErrors?.detail
          const plainError = res?.error?.response?.data?.error
          const normalizedPlainError = Array.isArray(plainError) ? plainError[0] : plainError
          setAlertText(
            apiErrorDetail
            || normalizedPlainError
            || res?.error?.response?.data?.message
            || "Check the Secondary Surveyor's booking time."
          )
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
}

  const discardSurveyorAssignment = assignmentId => {
    const params = {
      surveyor_assignment_id: assignmentId,
    }
    unassignSurveyor(baseUrl, id, params)
    .then(res => {
      if (res.status === 200) {
        reloadData()
      }
    })
    // eslint-disable-next-line no-console
    .catch(err => console.log(err))
  }

  const formatCheckedTime = assignment => {
    if (!assignment?.checkedInAt) return "";
    if (!assignment?.checkedOutAt) return `${format(new Date(assignment.checkedInAt), "p")} - In progress`;
    return `${format(new Date(assignment?.checkedInAt), "p")} - ${format(new Date(assignment?.checkedOutAt), "p")}`;
  }

  const checkInHandler = surveyorAssignmentId => {
    updateJobServiceTransition(baseUrl, id, "check-in", null, surveyorAssignmentId)
      .then(res => {
        if (res.status === 200) {
          reloadData()
        } else {
          toast.error(
            <div>
              <span><i className="fa fa-exclamation mr-2" /></span>
              <span className="ml-2">{res?.error?.response?.data?.errors[0]?.detail}</span>
            </div>,
          toastOptions
        )
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }

  const checkOutHandler = (surveyorAssignmentId, role) => {
    if (role === "assistant") {
      checkoutJobService(baseUrl, id, surveyorAssignmentId, null, "100%", null)
      .then(res => {
        if (res.status === 200) {
          reloadData()
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
    } else {
      handleOpenCheckOutModal(surveyorAssignmentId)
    }
  }

  useEffect(() => {
    setAssignments(originalAssignments)
  }, [scheduledAssignment])

  const assignPrimaryHandler = () => {
    const params = {
      booking_date: scheduledAt,
      start_at: scheduledAt,
      end_at: primarySurveyorAssignmentEndAt,
    }
    assignPrimarySurveyor(baseUrl, id, params)
    .then(res => {
      if (res.status === 200) {
        reloadData()
      }
    })
    // eslint-disable-next-line no-console
    .catch(err => console.log(err))
  }

  const unassignPrimaryHandler = () => {
    unassignPrimarySurveyor(baseUrl, id, primaryAssignmentOfDay.surveyorAssignmentId)
    .then(res => {
      if (res.status === 200) {
        reloadData()
      }
    })
    // eslint-disable-next-line no-console
    .catch(err => console.log(err))
  }

  const onStartTimeChange = date => {
    setStartAt(date)

    const hours = Math.floor(estimatedHours)
    const minutes = Math.floor((estimatedHours % 1) * 60)
    const newEndDate = new Date(date.getTime() + (hours * 60 * 60 * 1000) + (minutes * 60 * 1000))
    setEndAt(newEndDate)
  }

  const onEndTimeChange = date => {
    setEndAt(date)
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="assign-surveyor-modal">
      <Modal.Header closeButton>
        <h2 className="modal-title">Manage Surveyor</h2>
        <div className="assign-surveyor-modal__booking-date ml-3 pt-3">
          {format(new Date(scheduledAt), "dd MMM yyyy, EEEE haa")}
        </div>
      </Modal.Header>
      <div className="modal-body overflow-auto pb-4">
        { canAssign && (
          <div className="assign-surveyor-assign-form">
            <div className="assign-surveyor-datetimes">
              <div className="assign-surveyor-datetime-field">
                <label className="assign-surveyor-datetime-label" htmlFor="assign-surveyor-start-at">
                  Start time
                </label>
                <DatePicker
                  {...assignSurveyorDatePickerProps}
                  id="assign-surveyor-start-at"
                  required
                  selected={startAt}
                  onChange={date => {
                    onStartTimeChange(date)
                  }}
                  showTimeSelect
                  timeCaption="Time"
                  includeDates={[startAt]}
                  closeOnScroll={e => e.target === document}
                  className="form-control"
                  dateFormat="dd/MM/yyyy, hh:mm aa" />
              </div>
              <div className="assign-surveyor-datetime-field">
                <label className="assign-surveyor-datetime-label" htmlFor="assign-surveyor-end-at">
                  End time
                </label>
                <DatePicker
                  {...assignSurveyorDatePickerProps}
                  id="assign-surveyor-end-at"
                  required
                  selected={endAt}
                  onChange={date => onEndTimeChange(date)}
                  minDate={startAt}
                  minTime={startAt}
                  maxTime={new Date(startAt).setHours(23, 59, 59, 999)}
                  includeDates={[endAt]}
                  showTimeSelect
                  timeCaption="Time"
                  closeOnScroll={e => e.target === document}
                  className="form-control"
                  dateFormat="dd/MM/yyyy, hh:mm aa" />
              </div>
              { primarySurveyorId && !primaryAssignmentOfDay && (
                <div className="assign-surveyor-datetime-field assign-surveyor-datetime-field--action">
                  <button
                    className="btn btn-outlined-blue"
                    onClick={assignPrimaryHandler}
                    type="button">
                    Assign Primary
                  </button>
                </div>
              )}
            </div>
            <div className="assign-surveyor-add-row">
              <div className="col-5">
                <Select
                  isClearable
                  isOptionDisabled={option => option.disabled}
                  isSearchable
                  name="role"
                  onChange={e => setRole(e)}
                  value={role}
                  options={surveyorRoles}
                  placeholder="Select Role" />
              </div>

              <div className="col-5">
                <Select
                  isClearable
                  isOptionDisabled={option => option.disabled}
                  isSearchable
                  name="surveyor"
                  onChange={e => setSurveyor(e)}
                  value={surveyor}
                  options={filteredSurveyors}
                  placeholder="Select Surveyor" />
              </div>

              <div className="assign-surveyor-add-btn col-2">
                <button
                  className="btn btn-outlined-blue"
                  onClick={createSurveyorAssignment}
                  type="button">
                  Add
                </button>
              </div>
            </div>
          </div>
        ) }

        {!assignments?.length && (<div>No Surveyor Assigned yet.</div>)}
        {assignments?.length > 0 && (
          <div className="table-responsive">
          <table className="table table-listing">
            <tbody>
              {assignments.filter(ass => ass.surveyorId)?.map(ass => (
                <tr key={ass.surveyorAssignmentId}>
                  <td><div className={userInitColorRendering(ass.role)}>{ass?.surveyorInitials}</div></td>
                  <td>{ass?.surveyorName}</td>
                  <td>{ass?.roleLabel}</td>
                  {ass?.role !== "primary" ? (
                    <td>
                      { format(new Date(ass?.startAt), "p") }
                      {" "}
                      -
                      {" "}
                      { format(new Date(ass?.endAt), "p") }
                    </td>
                  ) : (<td></td>)}

                  <td>
                    {formatCheckedTime(ass)}
                  </td>
                  <td></td>
                  <td>
                    { ass?.checkedInAt && !ass?.checkedOutAt && (
                      <button
                        className="btn btn-link"
                        onClick={() => checkOutHandler(ass.surveyorAssignmentId, ass.role)}
                        type="button">
                        Check out
                      </button>
                    )}
                    { !ass?.checkedInAt && !ass?.checkedOutAt && (
                      <button
                        className="btn btn-link"
                        onClick={() => checkInHandler(ass.surveyorAssignmentId)}
                        type="button">
                        Check in
                      </button>
                    )}
                  </td>
                  <td>
                    { ass?.role !== "primary" && !ass?.checkedInAt && !ass?.checkedOutAt && (
                      <button
                        className="btn btn-link"
                        onClick={() => discardSurveyorAssignment(ass.surveyorAssignmentId)}
                        type="button">
                        Unassign
                      </button>
                    )}

                    { ass?.role === "primary" && !ass?.checkedInAt && !ass?.checkedOutAt && primaryAssignmentOfDay && (
                      <button
                        className="btn btn-link"
                        onClick={unassignPrimaryHandler}
                        type="button">
                        Unassign
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <SweetAlert
          show={showMessage}
          type="error"
          title={alertTitle}
          closeOnClickOutside
          allowEscape={false}
          onConfirm={resetError}>
          {alertText}
        </SweetAlert>
      </div>
    </Modal>
  )
  }
export default AssignSurveyorModal
